import React, { useEffect, Suspense } from 'react';
import { useLocation } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { useStores } from 'hooks/useStores';
import lazyWithRetry from 'utils/lazyWithRetry';
import Skeleton from 'ati-ui-react/components/Skeleton';
import TruckCardPageHelmet from 'components/TruckCardPage/components/TruckCardPageHelmet';
import PageContainer from 'generic/PageContainer';

import TransportName from './components/TransportName';
import Address from './components/Address';
import Price from './components/Price';
import PublicationInfo from './components/PublicationInfo';
import Gallery from './components/Gallery';
import MobileGallery from './components/MobileGallery';
import Sidebar from './components/Sidebar';
import OwnerInfo from './components/OwnerInfo';
import Description from './components/Description';
import AvailabilityDate from './components/AvailabilityDate';
import AccidentDescription from './components/AccidentDescription';
import CheckTransport from './components/CheckTransport';
import Related from './components/Related';
import NoMatch from './components/NoMatch';
import CallButton from './components/CallButton';
import QuoteData from './components/QuoteData';
import GovNumVin from './components/GovNumVin';
import TruckCardPageSkeleton from './components/TruckCardPageSkeleton';
import {
  TruckPageContainer,
  Row,
  BodyContainer,
  Body,
  DesktopSidebarContainer,
  MobileSidebarContainer,
  TitleAndPriceWrapper,
  AddressAndPublicationWrapper,
  SocialButtons,
  SideBarUserData,
  HorizontalUserData,
  MobileUserData,
  MobileHorizontUserData,
  PriceContainer,
} from './TruckCardPageStyles';

const Statistics = lazyWithRetry(() =>
  import(/* webpackChunkName: "StatisticsBlock" */ './components/Statistics'),
);

const TruckCardPage = observer(() => {
  const {
    advertsStore: {
      currentAdvert,
      currentAccount,
      currentAdvert: { _id, advert_item_type, is_owner },
      advertSearchStatus,
      advertLoaded,
      getCurrentAdvert,
      resetCurrentAdvert,
      isLoadingAdvert,
    },
    pagesStore: { setCurrentPage, setShowedBreadcrumbs },
    appStore: { width },
  } = useStores();

  const location = useLocation();
  const url = location.pathname;
  const isPurchase = currentAdvert.advert_type === 'purchase';

  useEffect(() => {
    setCurrentPage('advert');
    return () => {
      setCurrentPage('');
      resetCurrentAdvert();
    };
  }, []);

  useEffect(() => {
    getCurrentAdvert(url);
    window.scrollTo(0, 0);
  }, [url]);

  useEffect(() => {
    if (advertSearchStatus === 'match') {
      setShowedBreadcrumbs(true);
    }
  }, [advertSearchStatus]);

  if (advertSearchStatus === 'noMatch') {
    return <NoMatch />;
  }

  const contactsForAdvert = currentAccount?.contacts?.filter((contact) => {
    if (currentAdvert.contacts !== undefined) {
      return currentAdvert.contacts.includes(contact.id);
    }
    return true;
  });

  if (isLoadingAdvert || !advertLoaded) return <TruckCardPageSkeleton />;

  return (
    <PageContainer page="advert">
      <TruckPageContainer>
        <TruckCardPageHelmet />
        <Row>
          <TitleAndPriceWrapper>
            <TransportName />
            <PriceContainer>
              <Price isPurchase={isPurchase} />
              {/* <QuoteData /> */}
            </PriceContainer>
          </TitleAndPriceWrapper>
          <AddressAndPublicationWrapper>
            <Address />
            <PublicationInfo />
          </AddressAndPublicationWrapper>
        </Row>
        {is_owner && (
          <Suspense
            fallback={
              <Skeleton className="statistics" height={width >= 768 ? 340 : 579} borderRadius={4} />
            }
          >
            <Statistics id={_id} className="statistics" />
          </Suspense>
        )}
        <BodyContainer>
          <Body>
            <Gallery />
            <MobileGallery />

            {width <= 1024 && (
              <MobileSidebarContainer>
                <Sidebar />
                <GovNumVin />
                <SocialButtons>
                  <span className="descr">Поделиться</span>
                  <div
                    className="ya-share2"
                    data-services="vkontakte,odnoklassniki,viber,whatsapp,telegram"
                  />
                </SocialButtons>
              </MobileSidebarContainer>
            )}

            <OwnerInfo />
            <Description />
            <AvailabilityDate className="availability-date" />
            <AccidentDescription />

            {!isPurchase && <CheckTransport />}

            {advert_item_type.includes('road_train') && !isPurchase && (
              <HorizontalUserData>
                <ati-adv banner-size-id="13" />
              </HorizontalUserData>
            )}

            <MobileUserData>
              <span ati-userdata-info="doc-preset=loads_right_scroll" />
            </MobileUserData>

            <MobileHorizontUserData>
              <ati-adv banner-size-id="13" />
            </MobileHorizontUserData>
          </Body>
          <DesktopSidebarContainer>
            <Sidebar />
            <GovNumVin />

            <SocialButtons>
              <span className="descr">Поделиться</span>
              <div
                className="ya-share2"
                data-services="vkontakte,odnoklassniki,viber,whatsapp,telegram"
              />
            </SocialButtons>

            {/* Показываем баннер горизонтальный только для автопоезда */}
            {!advert_item_type.includes('road_train') && !isPurchase && (
              <SideBarUserData>
                <div min-screen-width="1024">
                  <span ati-userdata-info="doc-preset=loads_right_scroll" />
                </div>
              </SideBarUserData>
            )}
          </DesktopSidebarContainer>
        </BodyContainer>
        <CallButton contacts={contactsForAdvert} />
        <Related />
      </TruckPageContainer>
    </PageContainer>
  );
});

export default TruckCardPage;
